import { Zeeguu_API } from "./classDef";
import qs from "qs";
import { getPlatform } from "../utils/misc/browserDetection";

// Cache platform once at module load - avoids repeated detection on every activity log
const PLATFORM = getPlatform();

// Reader Opening Actions
Zeeguu_API.prototype.CLICKED_ARTICLE = "CLICKED ARTICLE";
Zeeguu_API.prototype.CLICKED_VIDEO = "CLICKED VIDEO";
Zeeguu_API.prototype.OPEN_ARTICLE = "OPEN ARTICLE";
Zeeguu_API.prototype.CLICKED_RESUME_ARTICLE = "CLICKED RESUME ARTICLE";
Zeeguu_API.prototype.ARTICLE_FOCUSED = "ARTICLE FOCUSED";
Zeeguu_API.prototype.OPEN_EXTENSION_INSTALLED = "OPEN EXTENSION INSTALLED";
Zeeguu_API.prototype.SEARCH_QUERY = "SEARCH QUERY";
Zeeguu_API.prototype.VIEWPORT_READER_SETTINGS = "VIEWPORT_READER_SETTINGS ";

// Preview overlay (Preview/Titles browsing modes). Time spent here also counts
// toward the surrounding browsing session; these events say which article it
// was spent on and how the preview ended.
Zeeguu_API.prototype.PREVIEW_OPENED = "PREVIEW OPENED";
Zeeguu_API.prototype.PREVIEW_CLOSED = "PREVIEW CLOSED";

// Reader Interaction Actions
Zeeguu_API.prototype.TRANSLATE_TEXT = "TRANSLATE TEXT";
Zeeguu_API.prototype.TRANSLATE_WORDS_IN_EXERCISE = "TRANSLATE WORDS IN EXERCISE";
Zeeguu_API.prototype.SPEAK_TEXT = "SPEAK TEXT";
Zeeguu_API.prototype.OPEN_ALTERMENU = "OPEN ALTERMENU";
Zeeguu_API.prototype.CLOSE_ALTERMENU = "CLOSE ALTERMENU";
Zeeguu_API.prototype.SEND_SUGGESTION = "SEND SUGGESTION";
Zeeguu_API.prototype.CHANGE_ORIENTATION = "CHANGE ORIENTATION";
Zeeguu_API.prototype.SCROLL = "SCROLL";
Zeeguu_API.prototype.STAR_ARTICLE = "STAR ARTICLE";
Zeeguu_API.prototype.LIKE_ARTICLE = "LIKE ARTICLE";
Zeeguu_API.prototype.USER_FEEDBACK = "USER FEEDBACK";
Zeeguu_API.prototype.EXTENSION_FEEDBACK = "EXTENSION FEEDBACK";
Zeeguu_API.prototype.DIFFICULTY_FEEDBACK = "DIFFICULTY FEEDBACK";
Zeeguu_API.prototype.PERSONAL_COPY = "PERSONAL COPY";
Zeeguu_API.prototype.WORDS_REVIEW = "WORDS_REVIEW";
Zeeguu_API.prototype.SUBSCRIBE_TO_SEARCH = "SUBSCRIBE_TO_SEARCH";
Zeeguu_API.prototype.UNSUBSCRIBE_FROM_SEARCH = "UNSUBSCRIBE_FROM_SEARCH";
Zeeguu_API.prototype.SUBSCRIBE_TO_EMAIL_NOTIFICATIONS = "SUBSCRIBE_TO_EMAIL_NOTIFICATIONS";
Zeeguu_API.prototype.UNSUBSCRIBE_FROM_EMAIL_NOTIFICATIONS = "UNSUBSCRIBE_FROM_EMAIL_NOTIFICATIONS";
Zeeguu_API.prototype.AUDIO_DISABLE = "AUDIO_DISABLE";

// Reader Closing Actions
Zeeguu_API.prototype.ARTICLE_CLOSED = "ARTICLE CLOSED";
Zeeguu_API.prototype.ARTICLE_UNFOCUSED = "ARTICLE LOST FOCUS";
// The reading clock stopping while the page has focus, and starting again
// (see useReadingSession): the teacher's focus strip tells idle from reading.
Zeeguu_API.prototype.ARTICLE_IDLE = "ARTICLE IDLE";
Zeeguu_API.prototype.ARTICLE_ACTIVE = "ARTICLE ACTIVE";
Zeeguu_API.prototype.ARTICLE_LIST_REQUESTED = "ARTICLES REQUESTED FROM ZEEGUU";
Zeeguu_API.prototype.TO_EXERCISES_AFTER_REVIEW = "TO EXERCISES AFTER REVIEW";

// Exercises Interaction
Zeeguu_API.prototype.SCHEDULED_EXERCISES_OPEN = "SCHEDULED_EXERCISES_OPEN";
Zeeguu_API.prototype.EXERCISE_FOCUSED = "EXERCISE FOCUSED";
Zeeguu_API.prototype.EXERCISE_UNFOCUSED = "EXERCISE LOST FOCUS";
Zeeguu_API.prototype.EXERCISE_IDLE = "EXERCISE IDLE";
Zeeguu_API.prototype.EXERCISE_ACTIVE = "EXERCISE ACTIVE";
Zeeguu_API.prototype.STAR_WORD = "STAR WORD";
Zeeguu_API.prototype.UNSTAR_WORD = "UNSTAR WORD";
Zeeguu_API.prototype.USER_SET_WORD_PREFERRED = "USER SET WORD TO STUDY";
Zeeguu_API.prototype.USER_SET_NOT_WORD_PREFERED = "USER SET NOT WORD STUDY";
Zeeguu_API.prototype.DELETE_WORD = "DELETE WORD";
Zeeguu_API.prototype.BACK_TO_READING = "BACK TO READING";
Zeeguu_API.prototype.COMPLETED_EXERCISES = "COMPLETED EXERCISES";
Zeeguu_API.prototype.KEEP_EXERCISING = "KEEP EXERCISING";

// Extension Interaction
Zeeguu_API.prototype.OPEN_POPUP = "OPEN POPUP";
Zeeguu_API.prototype.OPEN_CONTEXT = "OPEN CONTEXT";
Zeeguu_API.prototype.OPEN_MODAL = "OPEN MODAL";
Zeeguu_API.prototype.CLOSE_MODAL = "CLOSE MODAL";

// Audio Experiment
Zeeguu_API.prototype.AUDIO_EXP = "AUDIO_EXP";

// Rate limiting state for logUserActivity - prevents spam from buggy clients
const activityRateLimiter = {
  lastEventKey: null,
  lastEventTime: 0,
};

Zeeguu_API.prototype.logUserActivity = function (event, article_id = "", value = "", extra_data = "", source_id = "") {
  // TODO: think about -- what if you have a video_id?
  /**
   * Logs an activity
   *
   * @param {string} event - The type of event that occurred.
   * @param {string} [article_id=""] - The ID of the article associated with the event.
   * @param {string} [value=""] - Additional information about the event.
   * @param {string} [extra_data=""] - Extra data related to the event.
   * @param {string} [source_id=""] - The ID of source associated with the event.
   */

  // Rate limit: same event+value can only be logged once per 500ms
  const eventKey = `${event}:${value}:${source_id}`;
  const now = Date.now();
  if (eventKey === activityRateLimiter.lastEventKey && now - activityRateLimiter.lastEventTime < 500) {
    return;
  }
  activityRateLimiter.lastEventKey = eventKey;
  activityRateLimiter.lastEventTime = now;

  let event_information = {
    time: new Date().toJSON(),
    event: event,
    value: value,
    extra_data: extra_data,
    article_id: article_id,
    source_id: source_id,
    platform: PLATFORM,
  };

  const currentDate = new Date();
  const hours = String(currentDate.getHours()).padStart(2, "0");
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");
  const seconds = String(currentDate.getSeconds()).padStart(2, "0");

  console.log(`${hours}:${minutes}:${seconds} -- ` + event);

  // Use beacon to prevent "Load failed" errors when user navigates away
  this._postBeacon(`upload_user_activity_data`, qs.stringify(event_information));
};

Zeeguu_API.prototype.daysSinceLastUse = function (callback) {
  this._getPlainText("/days_since_last_use", callback);
};
